'use client';

import {LucideIcon} from 'lucide-react';
import {
  createElement,
  forwardRef,
  HTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import {VariantProps} from 'tailwind-variants';

import {cn, tv} from '../../libs/tailwind';
import {usePopperContext} from '../Popper';

export type MenuItemVariants = VariantProps<typeof menuItemStyles>;

export interface MenuItemProps extends HTMLAttributes<HTMLDivElement>, MenuItemVariants {
  children: ReactNode;
  icon?: LucideIcon | ReactElement;
  disabled?: boolean;
  autoClose?: boolean;
}

export const menuItemStyles = tv({
  base: [
    'px-2.5 py-1.5 pr-4 flex relative group/menu-item items-start gap-2 transition-all cursor-pointer rounded-sm',
    'ring-offset-bg-surface',
    'focus-visible:ring-2',
    'focus-visible:z-10',
  ],
  slots: {
    icon: 'size-4 relative z-10 shrink-0 opacity-60 transition-opacity',
  },
  variants: {
    variant: {
      default: {
        base: [
          'text-text-primary',
          'hover:bg-bg-secondary-hover',
          'hover:text-text-primary',
          'focus-visible:ring-brand-secondary',
        ],
        icon: ['group-hover/menu-item:opacity-100'],
      },
      danger: {
        base: [
          'text-error-primary',
          'hover:bg-error-primary/10',
          'focus-visible:ring-danger-primary',
        ],
        icon: ['group-hover/menu-item:opacity-100'],
      },
    },

    disabled: {
      true: {
        base: ['text-text-muted/50 pointer-events-none'],
      },
    },
  },
});

export const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(
  ({children, variant = 'default', icon, disabled = false, autoClose = true, ...rest}, ref) => {
    const {open, setOpen} = usePopperContext();
    const {base, icon: iconSlot} = menuItemStyles({variant, disabled});

    const isCustomIcon = useMemo(() => !('render' in (icon ?? {})), [icon]);

    const onClickItem = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        if (autoClose) {
          setOpen(!open);
        }
        rest?.onClick?.(e);
      },
      [open, autoClose, setOpen, rest?.onClick],
    );

    const onKeyDown = useCallback(
      (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClickItem(e as unknown as MouseEvent<HTMLDivElement>);
        }
      },
      [onClickItem],
    );

    return (
      <div
        {...rest}
        ref={ref}
        className={cn(base(), rest?.className)}
        tabIndex={0}
        role={!disabled ? 'option' : ''}
        onClick={onClickItem}
        onKeyDown={onKeyDown}
      >
        {isCustomIcon
          ? (icon as ReactElement)
          : createElement(icon as LucideIcon, {className: cn(iconSlot())})}
        <span className="w-full text-body-sm leading-4">{children}</span>
      </div>
    );
  },
);
